package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Objective
import com.tedd.db.Task
import kotlinx.coroutines.launch
import org.teddapp.project.parcours.PARCOURS_KINDS
import org.teddapp.project.parcours.ParcoursFilePicker
import org.teddapp.project.parcours.ParcoursPlan
import org.teddapp.project.ui.InkButton
import org.teddapp.project.ui.InkGhostButton
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddInkBlock
import org.teddapp.project.ui.TeddFineProgress
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.nav.NavIntent
import org.teddapp.project.ui.nav.Section

/** Part faite au dixième de pour cent près : « 0,4 % », « 12,5 % », « 100 % ». */
internal fun formatPermille(done: Int, planned: Int): String {
    if (planned <= 0) return "0 %"
    val permille = (done.toLong() * 1000 / planned).coerceIn(0, 1000).toInt()
    return if (permille % 10 == 0) "${permille / 10} %" else "${permille / 10},${permille % 10} %"
}

/** « 36 000 min » → « 600 h », « 90 min » → « 1 h 30 », « 20 min » → « 20 min ». */
internal fun formatMinutes(min: Int): String = when {
    min <= 0 -> "0 min"
    min < 60 -> "$min min"
    min % 60 == 0 -> "${min / 60} h"
    min < 600 -> "${min / 60} h ${(min % 60).toString().padStart(2, '0')}"
    else -> "${min / 60} h"
}

/**
 * Les deux façons d'apporter un parcours : un fichier, quand la plateforme sait en
 * ouvrir un, et le collage du JSON, toujours disponible.
 */
@Composable
internal fun ParcoursLoadButton(model: SkillsModel, size: TeddButtonSize = TeddButtonSize.Sm, kind: TeddButtonKind = TeddButtonKind.Ghost) {
    val scope = rememberCoroutineScope()
    val picker = ParcoursFilePicker.pick
    // Des gestes, pas de la lecture : hors sélection quand la fiche est copiable.
    DisableSelection {
        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            if (picker != null) {
                TeddButton(
                    text = "Charger un fichier",
                    onClick = { scope.launch { picker()?.let { model.readParcours(it) } } },
                    kind = kind,
                    size = size,
                )
            }
            TeddButton(text = "Coller un JSON", onClick = { model.pasteOpen = true }, kind = kind, size = size)
        }
    }
}

/**
 * Le parcours d'une compétence dans sa fiche : la suite des étapes, l'étape en cours
 * en bloc d'encre avec la prochaine chose à faire, et chaque étape dépliable jusqu'aux
 * micro. Une micro se lance en course de focus de sa durée.
 */
@Composable
internal fun ParcoursSection(
    st: SkillStats,
    model: SkillsModel,
    compact: Boolean,
    onNavigate: (Section) -> Unit,
    onStartFocus: ((task: Task, minutes: Int) -> Unit)?,
) {
    val steps = model.parcoursOf(st.skill.id)
    if (steps.isEmpty()) {
        // Pas encore de parcours : c'est ici qu'on l'apporte, pas dans l'en-tête.
        Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Text("PARCOURS", style = TeddType.Overline, color = TeddTokens.InkMuted)
            Text(
                "Aucun parcours pour cette compétence. Un fichier JSON apporte d'un coup les modules, " +
                    "les objectifs, les micro-tâches et les projets, dans l'ordre où ils se travaillent.",
                style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 14.sp,
            )
            ParcoursLoadButton(model, size = TeddButtonSize.Sm, kind = TeddButtonKind.Secondary)
        }
        return
    }
    val planned = steps.sumOf { it.plannedMinutes }
    val done = steps.sumOf { it.doneMinutes }
    val current = steps.firstOrNull { !it.done } ?: steps.last()

    Column(verticalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
        val core = model.coreParcoursOf(st.skill.id)
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("PARCOURS · ${formatMinutes(planned)}", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
            Text("${formatMinutes(done)} faites · ${formatPermille(done, planned)}", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.5.sp)
        }
        // Le noyau tient le temps réellement investi et le palier prouvé ; on les affiche, on ne les recalcule pas.
        if (core != null) {
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("${formatMinutes((core.secondesFocus / 60).toInt())} de focus · noyau", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.5.sp)
                core.palierAtteint?.let { Text("PALIER ${it.uppercase()}", style = TeddType.Overline, color = TeddTokens.LimeStrong) }
            }
        }
        // Continue, pas en jetons : sur un parcours de plusieurs centaines d'heures, une micro
        // cochée doit se voir tout de suite, et un jeton vaudrait des dizaines d'heures.
        TeddFineProgress(fraction = if (planned == 0) 0f else done.toFloat() / planned, height = 8.dp)

        // L'étape en cours : ce qu'il faut faire maintenant, en grand.
        current.nextTask?.let { next ->
            TeddInkBlock(
                eyebrow = "${current.step.code} · ${current.step.title}",
                title = next.title,
                subtitle = listOfNotNull(
                    next.estimated_minutes?.let { "${it} min" },
                    next.kind?.let { PARCOURS_KINDS[it] },
                    next.deliverable?.let { "Livrable : $it" },
                ).joinToString(" · "),
            ) {
                DisableSelection {
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.padding(top = 4.dp),
                    ) {
                        if (onStartFocus != null) {
                            InkButton(
                                text = "Lancer ${next.estimated_minutes ?: 25} min",
                                onClick = { onStartFocus(next, (next.estimated_minutes ?: 25L).toInt()) },
                                icon = { TeddIcon("play", size = 12.dp, color = TeddTokens.Ink) },
                            )
                        }
                        // Le même geste que dans Objectifs : faite sans passer par une course.
                        InkGhostButton(
                            text = "Fait",
                            onClick = { model.toggleTask(next) },
                            icon = { TeddIcon("check", size = 12.dp, color = TeddTokens.Bg, strokeWidth = 2.4.dp) },
                        )
                    }
                }
            }
        }

        steps.forEach { s ->
            StepRow(
                s = s,
                open = s.step.id in model.openSteps,
                onToggle = { model.toggleStep(s.step.id) },
                model = model,
                compact = compact,
                onNavigate = onNavigate,
                onStartFocus = onStartFocus,
            )
        }
        // Recharger un fichier corrigé : même geste, discret, en bas de la section.
        ParcoursLoadButton(model)
    }
}

@Composable
private fun StepRow(
    s: ParcoursStepStats,
    open: Boolean,
    onToggle: () -> Unit,
    model: SkillsModel,
    compact: Boolean,
    onNavigate: (Section) -> Unit,
    onStartFocus: ((Task, Int) -> Unit)?,
) {
    TeddCard(
        modifier = Modifier.fillMaxWidth().revealed(model, "step:${s.step.id}"),
        padding = PaddingValues(horizontal = 14.dp, vertical = 12.dp),
        tonal = s.isProject,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            modifier = Modifier.fillMaxWidth().clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onToggle),
        ) {
            Text(
                s.step.code,
                style = TeddType.Mono,
                color = if (s.done) TeddTokens.Success else TeddTokens.LimeStrong,
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.width(34.dp),
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(s.step.title, color = TeddTokens.Ink, fontSize = 13.5.sp, fontWeight = FontWeight.Medium, maxLines = if (open) 3 else 1, overflow = TextOverflow.Ellipsis)
                val detail = buildString {
                    append(formatMinutes(s.plannedMinutes))
                    append(" · ")
                    append(if (s.isProject) "${s.doneCount}/${s.totalCount} actions" else "${s.doneCount}/${s.totalCount} micro")
                    s.step.level_name?.let { append(" · palier ").append(it) }
                }
                Text(detail, style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.sp)
            }
            Text(
                if (s.done) "fait" else "${s.percent} %",
                style = TeddType.Mono,
                color = if (s.done) TeddTokens.Success else TeddTokens.InkMuted,
                fontSize = 12.sp,
            )
            // Un projet se suit là où il vit : le lien reste visible sans déplier.
            s.step.project_id?.let { pid ->
                DisableSelection {
                    Text(
                        "ouvrir",
                        style = TeddType.Overline,
                        color = TeddTokens.LimeStrong,
                        modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                            NavIntent.openProject(pid)
                            onNavigate(Section.Projects)
                        },
                    )
                }
            }
            TeddIcon("chevron", size = 12.dp, color = TeddTokens.InkMuted, modifier = Modifier.padding(start = 2.dp))
        }
        if (open) {
            Spacer(Modifier.height(10.dp))
            if (s.isProject) ProjectStepBody(s, model, onNavigate, onStartFocus)
            else ModuleStepBody(s, model, compact, onNavigate, onStartFocus)
        }
    }
}

@Composable
private fun ModuleStepBody(s: ParcoursStepStats, model: SkillsModel, compact: Boolean, onNavigate: (Section) -> Unit, onStartFocus: ((Task, Int) -> Unit)?) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        s.step.summary?.let {
            Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.5.sp)
        }
        s.objectives.forEach { obj -> ObjectiveBlock(obj, model, onNavigate, onStartFocus) }
    }
}

@Composable
internal fun ObjectiveBlock(obj: Objective, model: SkillsModel, onNavigate: (Section) -> Unit, onStartFocus: ((Task, Int) -> Unit)?) {
    // Ouvert ou replié : le modèle s'en souvient, et la recherche peut l'ouvrir elle-même.
    val open = obj.id in model.openObjectives
    Column(verticalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.revealed(model, "objective:${obj.id}")) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxWidth().clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { model.toggleObjective(obj.id) },
        ) {
            Text(obj.title, color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            Text("${obj.progress} %", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.sp)
            // L'objectif se suit aussi dans Objectifs : jalons, échéance, réflexion.
            DisableSelection {
                Text(
                    "ouvrir",
                    style = TeddType.Overline,
                    color = TeddTokens.LimeStrong,
                    modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                        NavIntent.openObjective(obj.id)
                        onNavigate(Section.Objectives)
                    },
                )
            }
        }
        if (open) {
            obj.description?.let {
                Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 12.5.sp)
            }
            // Les tâches posées directement sur l'objectif, avant celles de ses jalons.
            model.tasksOf(obj.id).forEach { t -> MicroRow(t, model, onStartFocus) }
            model.milestonesOf(obj.id).forEach { ms ->
                Column(verticalArrangement = Arrangement.spacedBy(4.dp), modifier = Modifier.padding(start = 8.dp, top = 4.dp)) {
                    Text(ms.title, color = TeddTokens.InkMuted, fontSize = 12.5.sp, fontWeight = FontWeight.Medium)
                    model.tasksOf(ms.id).forEach { t -> MicroRow(t, model, onStartFocus) }
                }
            }
        }
    }
}

/**
 * La case d'une tâche, celle d'Objectifs : bordure tant qu'elle est vide, dégradé une fois
 * faite. La zone de clic déborde la case, et le geste reste hors sélection de texte.
 */
@Composable
private fun TaskCheck(done: Boolean, onToggle: () -> Unit) {
    DisableSelection {
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onToggle),
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(18.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .then(
                        if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                        else Modifier.border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs)),
                    ),
                contentAlignment = Alignment.Center,
            ) {
                if (done) TeddIcon("check", size = 12.dp, color = TeddTokens.OnAction, strokeWidth = 2.5.dp)
            }
        }
    }
}

/** Une micro : sa case, son titre, sa durée, sa nature, et le geste qui lance la course. */
@Composable
private fun MicroRow(t: Task, model: SkillsModel, onStartFocus: ((Task, Int) -> Unit)?) {
    val done = model.isDone(t)
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        modifier = Modifier.fillMaxWidth().revealed(model, "task:${t.id}").padding(start = 2.dp),
    ) {
        TaskCheck(done) { model.toggleTask(t) }
        Text(
            t.title,
            color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
            fontSize = 12.5.sp,
            textDecoration = if (done) TextDecoration.LineThrough else null,
            modifier = Modifier.weight(1f).padding(end = 4.dp),
        )
        t.kind?.let { k ->
            Text(k.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, fontSize = 9.sp)
        }
        t.estimated_minutes?.let { min ->
            val clickable = onStartFocus != null && !done
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RFull))
                    .background(if (clickable) TeddTokens.LimeSoft else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                    .then(
                        if (clickable) Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onStartFocus!!(t, min.toInt()) }
                        else Modifier,
                    )
                    .padding(horizontal = 8.dp, vertical = 3.dp),
            ) {
                DisableSelection {
                    Text("$min min", color = if (clickable) TeddTokens.LimeStrong else TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun ProjectStepBody(s: ParcoursStepStats, model: SkillsModel, onNavigate: (Section) -> Unit, onStartFocus: ((Task, Int) -> Unit)?) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        s.step.level_name?.let { name ->
            Column {
                Text("PALIER · ${name.uppercase()}", style = TeddType.Overline, color = TeddTokens.LimeStrong)
                s.step.level_desc?.let { Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.5.sp) }
            }
        }
        s.step.summary?.let { Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.5.sp) }
        s.step.project_id?.let { pid ->
            model.tasksOf(pid).filter { it.parent_kind == "project" }.forEach { a -> ActionRow(a, model, onStartFocus) }
            DisableSelection {
                TeddButton(
                    text = "Ouvrir le projet",
                    onClick = { NavIntent.openProject(pid); onNavigate(Section.Projects) },
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Sm,
                )
            }
        }
    }
}

/** Une action de projet : titre, durée, puis livrable et critère lisibles séparément. */
@Composable
internal fun ActionRow(a: Task, model: SkillsModel, onStartFocus: ((Task, Int) -> Unit)?) {
    val done = model.isDone(a)
    Column(verticalArrangement = Arrangement.spacedBy(4.dp), modifier = Modifier.fillMaxWidth().revealed(model, "task:${a.id}")) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            TaskCheck(done) { model.toggleTask(a) }
            Text(
                a.title,
                color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
                textDecoration = if (done) TextDecoration.LineThrough else null,
                modifier = Modifier.weight(1f).padding(end = 4.dp),
            )
            a.estimated_minutes?.let { min ->
                val clickable = onStartFocus != null && !done
                DisableSelection {
                    Text(
                        formatMinutes(min.toInt()),
                        style = TeddType.Mono,
                        color = if (clickable) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                        fontSize = 11.5.sp,
                        modifier = if (clickable) Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onStartFocus!!(a, min.toInt().coerceAtMost(90)) } else Modifier,
                    )
                }
            }
        }
        a.deliverable?.let { LabeledLine("Livrable", it) }
        a.acceptance?.let { LabeledLine("Critère", it) }
    }
}

@Composable
private fun LabeledLine(label: String, text: String) {
    Row(horizontalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.padding(start = 32.dp)) {
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, fontSize = 9.sp, modifier = Modifier.width(52.dp).padding(top = 2.dp))
        Text(text, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 12.5.sp, modifier = Modifier.weight(1f))
    }
}

// ── Aperçu avant création ────────────────────────────────────────────────

/**
 * Ce que le fichier contient, avant d'écrire quoi que ce soit : les nombres, la suite
 * des étapes, les avertissements. Le bouton « Créer » est le seul qui écrive.
 */
@Composable
internal fun ParcoursImportDialog(plan: ParcoursPlan, model: SkillsModel, currentSkillName: String?, currentSkillId: String?) {
    val sameSkill = currentSkillName != null && currentSkillName.trim().equals(plan.competence.trim(), ignoreCase = true)
    val existingByName = model.skills.firstOrNull { it.name.trim().equals(plan.competence.trim(), ignoreCase = true) }
    Dialog(onDismissRequest = { if (!model.importing) model.cancelImport() }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .widthIn(max = 560.dp)
                .fillMaxWidth(0.94f)
                .heightIn(max = 640.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("PARCOURS À CRÉER", style = TeddType.Overline, color = TeddTokens.InkMuted)
            Text(plan.competence, style = TeddType.Title2, color = TeddTokens.Ink)
            Row(horizontalArrangement = Arrangement.spacedBy(22.dp)) {
                PreviewNumber("au total", formatMinutes(plan.totalMinutes))
                PreviewNumber("socle", formatMinutes(plan.socleMinutes))
                PreviewNumber("projets", formatMinutes(plan.projectMinutes))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(22.dp)) {
                PreviewNumber("modules", plan.modules.size.toString())
                PreviewNumber("projets", plan.projects.size.toString())
                PreviewNumber("objectifs", plan.objectiveCount.toString())
                PreviewNumber("tâches", plan.taskCount.toString())
                PreviewNumber("micro", plan.microCount.toString())
                PreviewNumber("actions", plan.actionCount.toString())
            }
            val kinds = plan.minutesByKind
            if (kinds.isNotEmpty()) {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    kinds.forEach { (k, min) ->
                        Box(
                            modifier = Modifier.clip(RoundedCornerShape(TeddTokens.RFull)).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull)).padding(horizontal = 10.dp, vertical = 5.dp),
                        ) {
                            Text("${PARCOURS_KINDS[k] ?: k} · ${formatMinutes(min)}", color = TeddTokens.InkMuted, fontSize = 11.5.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
            Column(modifier = Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                plan.steps.forEach { s ->
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.Top) {
                        Text(s.code, style = TeddType.Mono, color = TeddTokens.LimeStrong, fontSize = 12.sp, fontWeight = FontWeight.Bold, modifier = Modifier.width(34.dp))
                        Text(s.title, color = TeddTokens.Ink, fontSize = 13.sp, modifier = Modifier.weight(1f))
                        Text(formatMinutes(s.minutes), style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.5.sp)
                    }
                }
                if (plan.warnings.isNotEmpty()) {
                    Spacer(Modifier.height(6.dp))
                    Text("À VÉRIFIER (${plan.warnings.size})", style = TeddType.Overline, color = TeddTokens.Warning)
                    plan.warnings.take(30).forEach { w ->
                        Text("· $w", style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 12.5.sp)
                    }
                    if (plan.warnings.size > 30) Text("… et ${plan.warnings.size - 30} autres.", color = TeddTokens.InkMuted, fontSize = 12.sp)
                }
            }
            val target = when {
                sameSkill && currentSkillId != null -> "Le parcours s'ajoute à la compétence ouverte."
                existingByName != null -> "Une compétence « ${existingByName.name} » existe déjà : le parcours s'y ajoute."
                else -> "Une nouvelle compétence sera créée, cible ${formatMinutes(plan.totalMinutes)}."
            }
            Text(target, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.sp)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = if (model.importing) "Création…" else "Créer",
                    onClick = { model.confirmImport(if (sameSkill) currentSkillId else existingByName?.id) },
                    kind = TeddButtonKind.Primary,
                    enabled = !model.importing,
                )
                TeddButton(text = "Annuler", onClick = { model.cancelImport() }, kind = TeddButtonKind.Ghost, enabled = !model.importing)
            }
        }
    }
}

@Composable
private fun PreviewNumber(label: String, value: String) {
    Column {
        Text(value, style = TeddType.Numeral, color = TeddTokens.Ink, fontSize = 20.sp, lineHeight = 24.sp)
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

/** Les erreurs qui empêchent de lire le fichier : on les montre toutes, on n'écrit rien. */
@Composable
internal fun ParcoursErrorsDialog(errors: List<String>, onClose: () -> Unit) {
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .widthIn(max = 520.dp)
                .fillMaxWidth(0.94f)
                .heightIn(max = 520.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text("Le fichier ne peut pas être lu", style = TeddType.Title2, color = TeddTokens.Ink)
            Column(modifier = Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                errors.forEach { Text("· $it", style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.sp) }
            }
            TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Secondary)
        }
    }
}

/** Repli sans sélecteur de fichier : on colle le JSON. Le champ prend le focus à l'ouverture. */
@Composable
internal fun ParcoursPasteDialog(model: SkillsModel) {
    var text by remember { mutableStateOf("") }
    val focus = remember { FocusRequester() }
    LaunchedEffect(Unit) { focus.requestFocus() }
    Dialog(onDismissRequest = { model.pasteOpen = false }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .widthIn(max = 560.dp)
                .fillMaxWidth(0.94f)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text("Coller le parcours", style = TeddType.Title2, color = TeddTokens.Ink)
            Text("Le contenu du fichier JSON, tel quel.", style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.sp)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 160.dp, max = 320.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    // Cliquer n'importe où dans la zone donne le focus au champ.
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { focus.requestFocus() }
                    .padding(12.dp),
                contentAlignment = Alignment.TopStart,
            ) {
                BasicTextField(
                    value = text,
                    onValueChange = { text = it },
                    textStyle = TeddType.Mono.copy(color = TeddTokens.Ink, fontSize = 12.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    maxLines = 18,
                    modifier = Modifier.fillMaxWidth().focusRequester(focus),
                    decorationBox = { inner ->
                        if (text.isEmpty()) Text("{ \"meta\": { \"competence\": … }, \"ordre\": […], \"items\": { … } }", color = TeddTokens.InkSubtle, fontSize = 12.sp)
                        inner()
                    },
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = "Lire",
                    onClick = { model.pasteOpen = false; model.readParcours(text) },
                    kind = TeddButtonKind.Primary,
                    enabled = text.isNotBlank(),
                )
                TeddButton(text = "Annuler", onClick = { model.pasteOpen = false }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}


/**
 * La ligne qu'un résultat de recherche vient d'ouvrir : elle se met en vue et s'éclaire
 * un instant, le temps de la trouver des yeux ; puis l'éclairage s'éteint seul.
 */
@OptIn(androidx.compose.foundation.ExperimentalFoundationApi::class)
@Composable
internal fun Modifier.revealed(model: SkillsModel, key: String): Modifier {
    val requester = remember { androidx.compose.foundation.relocation.BringIntoViewRequester() }
    val on = model.revealed == key
    val tick = model.revealTick
    LaunchedEffect(on, tick) {
        if (on) {
            // La ligne vient peut-être d'être dépliée : on la laisse se poser avant de défiler.
            kotlinx.coroutines.delay(60)
            runCatching { requester.bringIntoView() }
            kotlinx.coroutines.delay(2600)
            model.clearReveal(key)
        }
    }
    val tint by androidx.compose.animation.animateColorAsState(if (on) TeddTokens.LimeSoft else androidx.compose.ui.graphics.Color.Transparent, label = "reveal")
    return this
        .bringIntoViewRequester(requester)
        .background(tint, RoundedCornerShape(TeddTokens.RSm))
}
